import dayjs from "dayjs";

const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';
const formatDisplayDate = (date?: string) => {
  return date ? dayjs(date).format(DISPLAY_DATE_FORMAT) : '';
};

const formatDisplayDateTime = (date?: string) => {
  return date ? dayjs(date).format('MMM D, h:mm A') : '';
};

const formatDisplayTime = (date?: string) => {
  return date ? dayjs(date).format('h:mma') : '';
};

/**
 * Formats a Date object to YYYY-MM-DD string without timezone conversion
 * This shows the exact date that was stored, avoiding timezone offset issues
 *
 * Note: This function assumes the Date object was created from an ISO date string
 * and uses UTC methods to get the original date components
 */
const formatDateForInput = (date: Date): string => {
  // Use UTC methods to get the actual date that was stored
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a Date object to YYYY-MM-DD string using local timezone
 * This shows the date in the user's local timezone
 */
const formatDateForInputLocal = (date: Date): string => {
  // Use local methods to get the date in user's timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Creates a Date object from a date string in local timezone
 * This avoids timezone offset issues when parsing HTML date input values
 */
const createLocalDate = (dateString: string): Date => {
  return new Date(dateString + 'T00:00:00');
};

/**
 * Compares two date strings without timezone conversion
 * Returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
const compareDateStrings = (date1: string, date2: string): number => {
  if (date1 < date2) return -1;
  if (date1 > date2) return 1;
  return 0;
};

/**
 * Finds the earliest date string from an array of date strings
 */
const getEarliestDateString = (dates: string[]): string => {
  return dates.reduce((earliest, current) =>
    compareDateStrings(current, earliest) < 0 ? current : earliest
  );
};

/**
 * Finds the latest date string from an array of date strings
 */
const getLatestDateString = (dates: string[]): string => {
  return dates.reduce((latest, current) =>
    compareDateStrings(current, latest) > 0 ? current : latest
  );
};

/**
 * Formats a Date object for display without applying timezone conversion
 * This shows the exact date as it was stored, regardless of user's timezone
 *
 * Note: This function assumes the Date object was created from an ISO date string
 * and extracts the UTC date components to avoid timezone shifts
 */
const formatDateForDisplay = (date: Date): string => {
  // Use UTC methods to get the actual date that was stored
  // This avoids timezone conversion issues
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();

  // Format as M/D/YYYY (US format) without timezone adjustment
  return `${month}/${day}/${year}`;
};

export {
  formatDisplayDate,
  formatDisplayDateTime,
  formatDisplayTime,
  formatDateForInput,
  formatDateForInputLocal,
  formatDateForDisplay,
  createLocalDate,
  compareDateStrings,
  getEarliestDateString,
  getLatestDateString,
  DISPLAY_DATE_FORMAT,
};
